import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { startWith, first } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import {
  CCAuthService,
  CCPicklistService,
  GoogleOAuthService,
  getIntendedRoute,
  clearIntendedRoute,
} from '@maids/cc-erp-services';
import { CacheService } from '../../services/cache.service';
import { environment } from 'src/environments/environment';
import { assets } from '@maids/cc-lib/utils';
import { trigger, style, animate, transition } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { CCInputComponent } from '@maids/cc-lib/input';

@Component({
  selector: 'app-new-login',
  templateUrl: './new-login.component.html',
  styleUrls: ['./new-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate(1000, style({ opacity: 1 })),
      ]),
      transition(':leave', [animate(1000, style({ opacity: 0 }))]),
    ]),
  ],
})
export class NewLoginComponent implements OnInit, OnDestroy {
  quotes: string[] = [];
  currentIndex: number = 0;
  currentQuote: string = '';
  showQuoteTime: number = 12000; // 12 seconds

  smileyDecimal: string = '🙂';

  quoteAnimationState = '';
  isLandscape: boolean = false;
  isResponsiveLandscape: boolean = false;

  googleConfig = {
    scope: 'openid email',
    redirectUri: environment.googleRedirectUri,
    state: {
      app: 'new-erp',
      host: window.location.origin,
    },
  };

  constructor(
    private _auth: CCAuthService,
    private _router: Router,
    private _picklistService: CCPicklistService,
    private _cdr: ChangeDetectorRef,
    private _googleOAuth: GoogleOAuthService,
    @Inject(DOCUMENT) private document: Document,
    private _route: ActivatedRoute,
    private _cacheService: CacheService
  ) {}

  private readonly _currentNavigation = this._router.getCurrentNavigation();
  private readonly _subscriptions = new Subscription();

  ngOnInit(): void {
    this.getQuotes();
    this.checkScreenOrientation();
    // Check if this is a Google OAuth callback
    if (
      this._route.snapshot.queryParams['code'] &&
      this._route.snapshot.queryParams['state']
    ) {
      this.handleGoogleOAuthCallback();
    }
  }

  getQuotes() {
    this._picklistService
      .getPicklist({
        code: 'login_quotes',
      })
      .subscribe((res) => {
        this.quotes = res.map(
          (item: { label: string; id: number }) => item.label
        );
        if (this.quotes.length) {
          const maxStartIndex = Math.min(15, this.quotes.length - 1);
          const startIndex = Math.floor(Math.random() * (maxStartIndex + 1));
          // Seed so the first interval tick shows `startIndex` (keeps current behavior).
          this.currentIndex =
            (startIndex - 1 + this.quotes.length) % this.quotes.length;
        }
        this.iterateOverQuotes();
      });
  }

  iterateOverQuotes() {
    if (!this.quotes.length) return;
    this._subscriptions.add(
      interval(this.showQuoteTime)
        .pipe(startWith(0))
        .subscribe(() => {
          this.currentIndex = (this.currentIndex + 1) % this.quotes.length;
          this.currentQuote = '';
          this._cdr.detectChanges();
          setTimeout(() => {
            this.currentQuote = this.quotes[this.currentIndex];
            this._cdr.detectChanges();
          }, 2000);
        })
    );
  }

  imageUrl(path: string) {
    return environment.production && !environment.newErp
      ? assets.getBasePath() + `new-theme-pages/assets/images/${path}`
      : assets.getBasePath() + `assets/images/${path}`;
  }

  clearAllCookies() {
    const cookies = this.document.cookie.split(';');
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      this.document.cookie =
        name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  }



  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenOrientation();
  }

  private checkScreenOrientation() {
    this.isLandscape =
      window.innerWidth > window.innerHeight && window.innerWidth < 880;
    this.isResponsiveLandscape =
      window.innerWidth > window.innerHeight &&
      window.innerWidth > 880 &&
      window.innerWidth < 1400;
  }

  /**
   * Start Google OAuth authentication
   */
  startGoogleAuth(): void {
    this.onGoogleAuthStart();
    this._googleOAuth.startAuthorizationCodeFlow(this.googleConfig);
  }

  /**
   * Handle Google OAuth callback on page load
   */
  private handleGoogleOAuthCallback(): void {
    this._subscriptions.add(
      this._googleOAuth.handleOAuthCallback().subscribe({
        next: (user) => {
          if (user) {
            this._cacheService.clearERPMenuCache();
            this.onGoogleAuthSuccess(user);
          }
        },
        error: (error) => this.onGoogleAuthError(error),
      })
    );
  }

  /**
   * Handle successful Google authentication
   */
  async onGoogleAuthSuccess(user: any): Promise<void> {
    this._subscriptions.add(
      this._auth.loggedUser$.pipe(first()).subscribe((loggedInUser: any) => {
        // Keep parity with the legacy login behavior (special-case freedom users)
        if (loggedInUser?.freedomUser === true) {
          window.location.href = '/freedom-operator/v2/dashboard';
          return;
        }

        // IMPORTANT: OAuth callback is a full page load, so Router navigation extras state is lost.
        // Prefer the stored "intended route" (set by guards) after successful login.
        const intendedRoute = getIntendedRoute();
        if (intendedRoute) {
          clearIntendedRoute();

          // Use hard navigation because the intended route may belong to another
          // app/micro-frontend (Router.navigateByUrl would keep us in this Angular app).
          // Also handles routes that were stored without a leading slash.
          const normalized =
            /^https?:\/\//i.test(intendedRoute) || intendedRoute.startsWith('/')
              ? intendedRoute
              : `/${intendedRoute}`;
          window.location.href = normalized;
          return;
        }
        const destinationPage = sessionStorage.getItem('destinationPage');
        if (destinationPage) {
          sessionStorage.removeItem('destinationPage');
          window.location.href = destinationPage;
          return;
        }

        // Fallback
        this._router.navigateByUrl('/');
      })
    );
  }

  /**
   * Handle Google authentication error
   */
  onGoogleAuthError(error: any): void {
    console.error('Google authentication error:', error);

    // Handle different error types with user-friendly messages
    if (error.includes?.('popup_blocked')) {
      console.warn('Popup was blocked for Google sign-in');
    } else if (error.includes?.('access_denied')) {
      console.warn('Google sign-in was cancelled');
    } else {
      console.warn('Google authentication failed. Please try again.');
    }

    // Clear any cookies on auth failure
    this.clearAllCookies();
  }

  /**
   * Handle Google authentication start
   */
  onGoogleAuthStart(): void {
    console.log('Google authentication started');
  }
}
